import React, { useCallback } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { COLORS, FONT } from "../../shared/styles";
import {
  MAX_PRE_CHAT_FIELDS,
  getPreChatFieldLabelError,
  normalizePreChatFields,
} from "./constants";
import SettingsToggle from "./SettingsToggle";

const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: "36px",
    borderRadius: "8px",
    fontFamily: FONT.family,
    fontSize: "14px",
    color: COLORS.textPrimary,
    background: COLORS.white,
    "& fieldset": { borderColor: "#B7C2CC" },
    "&:hover fieldset": { borderColor: "#B7C2CC" },
    "&.Mui-focused fieldset": { borderColor: COLORS.primary },
  },
  "& .MuiOutlinedInput-input": { py: 0, px: "12px" },
};

const inputErrorSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    "& fieldset": { borderColor: COLORS.error },
    "&:hover fieldset": { borderColor: COLORS.error },
    "&.Mui-focused fieldset": { borderColor: COLORS.error },
  },
};

function ToggleChip({ label, checked, disabled, onChange, ariaLabel }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "8px",
        opacity: disabled ? 0.5 : 1,
        width: "100%",
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          color: COLORS.textSecondary,
          fontFamily: FONT.family,
          fontSize: "13px",
          fontWeight: 400,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <SettingsToggle
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        ariaLabel={ariaLabel}
      />
    </Box>
  );
}

function FieldRow({
  field,
  onChange,
  onRemove,
  enabledToggleDisabled = false,
  removeDisabled = false,
}) {
  const labelError = field.locked ? "" : getPreChatFieldLabelError(field.label);

  return (
    <Box
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 108px 108px 28px",
        alignItems: "start",
        columnGap: "12px",
        py: "10px",
        borderBottom: `1px solid ${COLORS.borderLight}`,
      }}
    >
      <Box>
        <TextField
          value={field.label}
          disabled={field.locked}
          placeholder="Field label"
          error={Boolean(labelError)}
          onChange={(e) => onChange({ label: e.target.value })}
          onBlur={(e) => {
            if (field.locked) return;
            const trimmed = e.target.value.trim();
            if (trimmed !== field.label) {
              onChange({ label: trimmed });
            }
          }}
          sx={labelError ? inputErrorSx : inputSx}
        />
        {labelError ? (
          <Typography
            sx={{
              mt: "4px",
              color: COLORS.error,
              fontFamily: FONT.family,
              fontSize: "11px",
              lineHeight: "14px",
            }}
          >
            {labelError}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ pt: "2px" }}>
        <ToggleChip
          label="Enabled"
          checked={field.enabled}
          disabled={enabledToggleDisabled}
          onChange={(enabled) => onChange({ enabled })}
          ariaLabel={`${field.label || "Field"} enabled`}
        />
      </Box>

      <Box sx={{ pt: "2px" }}>
        <ToggleChip
          label="Required"
          checked={field.required}
          disabled={!field.enabled}
          onChange={(required) => onChange({ required })}
          ariaLabel={`${field.label || "Field"} required`}
        />
      </Box>

      <Box
        sx={{
          width: 28,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {onRemove ? (
          <Box
            component="button"
            type="button"
            onClick={onRemove}
            disabled={removeDisabled}
            aria-label={`Remove ${field.label || "field"}`}
            title={removeDisabled ? "At least one field must remain enabled" : undefined}
            sx={{
              width: 28,
              height: 28,
              border: "none",
              background: "none",
              color: COLORS.textSecondary,
              fontFamily: FONT.family,
              fontSize: "20px",
              lineHeight: 1,
              cursor: removeDisabled ? "not-allowed" : "pointer",
              opacity: removeDisabled ? 0.4 : 1,
              p: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { color: removeDisabled ? COLORS.textSecondary : COLORS.error },
            }}
          >
            ×
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

function PreChatFormBuilder({ fields, onChange }) {
  const normalized = normalizePreChatFields(fields);
  const enabledFieldCount = normalized.filter((field) => field.enabled).length;

  const handleFieldChange = useCallback(
    (fieldId, patch) => {
      const currentField = normalized.find((field) => field.id === fieldId);
      if (
        patch.enabled === false
        && currentField?.enabled
        && enabledFieldCount === 1
      ) {
        return;
      }

      const next = normalized.map((field) =>
        field.id === fieldId ? { ...field, ...patch } : field,
      );
      onChange(normalizePreChatFields(next));
    },
    [enabledFieldCount, normalized, onChange],
  );

  const handleAddField = useCallback(() => {
    if (normalized.length >= MAX_PRE_CHAT_FIELDS) return;

    const nextIndex = normalized.length + 1;
    onChange(
      normalizePreChatFields([
        ...normalized,
        {
          id: `custom_${Date.now()}`,
          label: `Field ${nextIndex}`,
          enabled: true,
          required: false,
          locked: false,
        },
      ]),
    );
  }, [normalized, onChange]);

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "8px",
        border: `1px solid ${COLORS.border}`,
        background: COLORS.white,
        px: "16px",
        pt: "14px",
        pb: "12px",
      }}
    >
      <Typography
        sx={{
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "16px",
          fontWeight: 500,
          lineHeight: "18px",
          mb: "4px",
        }}
      >
        Form fields
      </Typography>

      {normalized.map((field) => (
        <FieldRow
          key={field.id}
          field={field}
          enabledToggleDisabled={field.enabled && enabledFieldCount === 1}
          removeDisabled={field.enabled && enabledFieldCount === 1}
          onChange={(patch) => handleFieldChange(field.id, patch)}
          onRemove={
            field.locked
              ? undefined
              : () => {
                  onChange(
                    normalizePreChatFields(
                      normalized.filter((item) => item.id !== field.id),
                    ),
                  );
                }
          }
        />
      ))}

      {normalized.length < MAX_PRE_CHAT_FIELDS ? (
        <Box
          component="button"
          type="button"
          onClick={handleAddField}
          sx={{
            mt: "10px",
            border: "none",
            background: "none",
            color: COLORS.primary,
            fontFamily: FONT.family,
            fontSize: "13px",
            fontWeight: 500,
            cursor: "pointer",
            p: 0,
            alignSelf: "flex-start",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          + Add field
        </Box>
      ) : null}
    </Box>
  );
}

export default React.memo(PreChatFormBuilder);
